/**
 * "Zum Home-Bildschirm hinzufuegen" - der Weg zum Vollbild auf dem iPhone
 * (Produktentscheidung vom 15.10.2026).
 *
 * Robin: "Auf Safari am Handy wechselt man weder automatisch in den Fullscreen
 * noch ueber den Knopf im Menue oder im Game."
 *
 * Der Grund liegt nicht bei uns: Auf dem iPhone erlaubt Safari Vollbild nur
 * fuer Videos, nie fuer eine Seite - und jeder Browser auf dem iPhone benutzt
 * dieselbe Technik darunter. Auf dem iPad geht es seit iPadOS 16.4, auf
 * Android ohnehin.
 *
 * Es bleibt genau ein Weg: Die Seite auf den Home-Bildschirm legen und von
 * dort starten. Dann laeuft sie ohne Adressleiste und ohne Browserleisten -
 * dasselbe Bild wie Vollbild. Damit das wirkt, braucht die Seite ein
 * Manifest und ein Symbol; beides liegt unter apps/client/public.
 *
 * Robins Vorgabe fuer den Hinweis: "lege diese Info direkt ueber alles
 * drueber, bevor man ueberhaupt im Menue spielen kann. Dieser Hinweis darf
 * natuerlich nur kommen, wenn man mit Safari das Spiel oeffnet und auch nur
 * am Handy."
 *
 * Gefragt wird deshalb nicht nach dem NAMEN des Browsers, sondern nach
 * seinem KOENNEN. Eine Namensabfrage ("steht Safari im Kennzeichen?") geht
 * regelmaessig schief: Jeder Browser auf dem iPhone schreibt Safari hinein,
 * und Chrome am Rechner tut es auch. Die Frage "kann dieses Geraet Vollbild?"
 * trifft dagegen genau die Geraete, die den Hinweis brauchen - und das iPad
 * faellt von selbst heraus, weil es Vollbild kann.
 */

/**
 * Soll der Hinweis ueber allem liegen?
 *
 * Vier Bedingungen, jede mit einem Grund:
 *
 *   - Nur am Finger-Geraet. Einen Home-Bildschirm gibt es am Rechner nicht.
 *   - Nur, wo Vollbild NICHT geht. Wer den Knopf hat, braucht den Umweg
 *     nicht - das schliesst iPad und Android ein.
 *   - Nicht, wenn wir schon vom Home-Bildschirm laufen. Dann ist der Rat
 *     schon befolgt, und der Hinweis waere nur noch im Weg.
 *   - Nicht, wenn er schon einmal weggetippt wurde. Er erklaert etwas
 *     einmal; danach steht dasselbe dauerhaft in den Einstellungen.
 */
export function zeigeStartbildschirmHinweis(stand: {
  /** Wird das Geraet mit dem Finger bedient? */
  tippflaeche: boolean;
  /** Kann der Browser Vollbild? */
  vollbildMoeglich: boolean;
  /** Laeuft das Spiel schon vom Home-Bildschirm? */
  schonAlsApp: boolean;
  /** Hat der Spieler den Hinweis schon einmal weggetippt? */
  schonGelesen: boolean;
}): boolean {
  if (!stand.tippflaeche) return false;
  if (stand.vollbildMoeglich) return false;
  if (stand.schonAlsApp) return false;

  return !stand.schonGelesen;
}

/**
 * Platz im Browserspeicher fuer "schon gelesen".
 *
 * Der dauerhafte Speicher und nicht der der Sitzung: Der Hinweis soll beim
 * naechsten Oeffnen nicht wieder im Weg stehen. Wer ihn noch einmal lesen
 * will, findet denselben Text in den Einstellungen.
 */
const SPEICHER_SCHLUESSEL = 'dueline.startbildschirmHinweis';

/** Wurde der Hinweis schon einmal weggetippt? */
export function hinweisSchonGelesen(): boolean {
  try {
    return localStorage.getItem(SPEICHER_SCHLUESSEL) === 'gelesen';
  } catch {
    // Privates Fenster oder gesperrter Speicher: dann eben jedes Mal.
    return false;
  }
}

/** Merkt sich, dass der Hinweis weg darf. */
export function merkeHinweisGelesen(): void {
  try {
    localStorage.setItem(SPEICHER_SCHLUESSEL, 'gelesen');
  } catch {
    // Dann bleibt es beim naechsten Mal eben noch einmal stehen.
  }
}
